import {
  Autocomplete,
  AutocompleteChangeReason,
  CircularProgress,
  TextField,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { translateKey } from 'src/locales/translate';
import { renderMedicineSearchOption } from 'src/sections/@dashboard/profile/components/medication-form/MedicineSearchOptionRow';
import {
  MedicineSearchOption,
  isSameMedicineOption,
  keepServerOrder,
  medicineOptionGroup,
  medicineOptionLabel,
} from 'src/sections/@dashboard/profile/components/medication-form/medicineSearchOptions';
import { useMedicineSearchOptions } from 'src/sections/@dashboard/profile/components/medication-form/useMedicineSearchOptions';

interface AssociatedMedicineSearchFieldProps {
  /** A Facility form with the Warehouse enabled: the clinic's drugs lead (#690). */
  warehouseAware: boolean;
  onPick: (option: MedicineSearchOption) => void;
  onTypedName: (name: string) => void;
}

/**
 * "Dodaj lek": the shared medicine search as a freeSolo field. A pick or a
 * typed name hands off to the editor, which opens the line's form; the field
 * then starts over empty.
 */
export default function AssociatedMedicineSearchField({
  warehouseAware,
  onPick,
  onTypedName,
}: AssociatedMedicineSearchFieldProps): React.ReactElement {
  const [inputValue, setInputValue] = useState('');
  const [resetKey, setResetKey] = useState(0);
  /**
   * The last text the user actually typed. `inputValue` is not it: MUI resets
   * that to the option's label before handing us the selection, so by then the
   * typed name is gone.
   */
  const lastTypedRef = useRef('');
  const { options, isLoading } = useMedicineSearchOptions({
    query: inputValue,
    warehouseAware,
  });

  const startOver = (): void => {
    setInputValue('');
    setResetKey((key) => key + 1);
  };

  const commitTyped = (typed: string): void => {
    const name = typed.trim();
    if (!name) return;
    onTypedName(name);
    startOver();
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    value: MedicineSearchOption | string | null,
    reason: AutocompleteChangeReason,
  ): void => {
    if (!value) return;
    if (typeof value === 'string') {
      commitTyped(value);
      return;
    }
    // `autoSelect` hands back whichever option the pointer last crossed, so a
    // blur on the way out of the field would swap the typed name for a product
    // nobody chose. Only a real pick counts.
    if (reason === 'blur') {
      commitTyped(lastTypedRef.current);
      return;
    }
    onPick(value);
    startOver();
  };

  return (
    <Autocomplete<MedicineSearchOption | string, false, false, true>
      key={resetKey}
      freeSolo
      // Leaving the field is the commit; MUI otherwise only takes typed
      // text on Enter, which is the one thing a vet will not know to press.
      autoSelect
      options={options}
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(_event, next, reason) => {
        setInputValue(next);
        if (reason === 'input') lastTypedRef.current = next;
      }}
      groupBy={warehouseAware ? medicineOptionGroup : undefined}
      filterOptions={warehouseAware ? keepServerOrder : undefined}
      getOptionLabel={medicineOptionLabel}
      isOptionEqualToValue={isSameMedicineOption}
      onChange={handleChange}
      renderOption={renderMedicineSearchOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translateKey('hardcoded.dodaj_lek')}
          placeholder={translateKey('hardcoded.wyszukaj_lek_lub_wpisz_nazwe')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="inherit" size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
